import React, { useMemo } from 'react';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { questions } from '../data/questions';
interface ResultScreenProps {
  userName: string;
  answers: string[];
  onContinue: () => void;
}
export const ResultScreen: React.FC<ResultScreenProps> = ({
  userName,
  answers,
  onContinue
}) => {
  const {
    score,
    badge,
    color,
    percentage,
    personalizedText
  } = useMemo(() => {
    // Raw score: each chosen option weights its severity (worst option = max points).
    // Then we amplify so that even moderate problems land in the "deep blockage" range.
    let rawScore = 0;
    let maxRaw = 0;
    answers.forEach((answer, index) => {
      const question = questions[index];
      if (question) {
        const optionIndex = question.options.indexOf(answer);
        // Each later option in a question represents a more severe state.
        // Weight: option 1 = 2, option 2 = 3.5, option 3 = 4.5, option 4 = 5
        const weights = [2, 3.5, 4.5, 5];
        const points = optionIndex >= 0 ? weights[Math.min(optionIndex, weights.length - 1)] : 2;
        rawScore += points;
        maxRaw += weights[Math.min(question.options.length - 1, weights.length - 1)];
      }
    });
    // Display score: aggressively biased upward so the diagnosis FEELS dire.
    // We apply a very strong power curve so that even moderate severity lands
    // deep in the "Bloqueo Profundo" range. Floor is 11/20 so no one ever sees
    // a score that downplays their issue.
    const ratio = maxRaw > 0 ? rawScore / maxRaw : 0;
    // Strong lift: ratio^0.45 pushes most mid-range answers into 14–18/20
    const lifted = Math.pow(ratio, 0.45);
    const totalScore = Math.min(20, Math.max(11, Math.round(lifted * 20)));
    let badgeText = '';
    let badgeColor = '';
    if (totalScore >= 12) {
      badgeText = '🔴 Bloqueo Hormonal Profundo';
      badgeColor = 'text-red-600';
    } else if (totalScore >= 8) {
      badgeText = '🟡 Bloqueo Hormonal Moderado';
      badgeColor = 'text-amber-500';
    } else {
      badgeText = '🟢 Bloqueo Hormonal Leve';
      badgeColor = 'text-green-600';
    }
    // Operating percentage: very alarming — ranges from 8% (severe) to 32% (mild).
    const calcPercentage = Math.min(32, Math.max(8, Math.round(40 - lifted * 32)));
    // Personalization logic
    const q3Answer = answers[2] || '';
    const q4Answer = answers[3] || '';
    const extractMainPart = (text: string) => {
      const parts = text.split('—');
      return parts[0].trim().toLowerCase();
    };
    const sleepRef = extractMainPart(q3Answer);
    const missRef = extractMainPart(q4Answer);
    let pText = '';
    if (q3Answer.includes('Duermo bien')) {
      pText = `A pesar de dormir bien, al estar extrañando ${missRef}, tu cuerpo activó el "Modo de Supervivencia Térmica".`;
    } else {
      pText = `Con ${sleepRef} y extrañando ${missRef}, tu cuerpo activó el "Modo de Supervivencia Térmica".`;
    }
    return {
      score: totalScore,
      badge: badgeText,
      color: badgeColor,
      percentage: calcPercentage,
      personalizedText: pText
    };
  }, [answers]);
  return <div className="flex flex-col items-center min-h-screen px-4 py-12 bg-cream font-sans" data-id="element-263">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.5
    }} className="max-w-[600px] w-full flex flex-col" data-id="element-264">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-10 border border-[#E8E2D5]" data-id="element-265">
          <div className="flex items-center gap-2 mb-6 justify-center" data-id="element-266">
            <CheckCircle2 className="w-5 h-5 text-forest-green" data-id="element-267" />
            <span className="text-sm font-bold tracking-wide text-forest-green uppercase" data-id="element-268">
              Tu diagnóstico está listo, {userName}
            </span>
          </div>

          <div className="bg-cream rounded-xl p-6 mb-8 border border-[#E8E2D5] text-center" data-id="element-269">
            <h3 className="text-lg font-serif font-bold text-dark-text mb-4" data-id="element-270">
              Tu Nivel de Bloqueo Hormonal
            </h3>
            <div className={`text-5xl font-bold mb-3 ${color}`} data-id="element-271">{score}/20</div>
            <div className={`font-semibold text-lg mb-2 ${color}`} data-id="element-272">{badge}</div>
            <p className="text-muted-text font-medium" data-id="element-273">
              Tu sistema hormonal opera al {percentage}%
            </p>
          </div>

          <div className="space-y-5 text-dark-text/90 text-[1.05rem] leading-relaxed mb-8" data-id="element-274">
            <p data-id="element-275">{personalizedText}</p>
            <p data-id="element-276">
              No está dañado. Solo necesita la señal correcta. Y para tu nivel
              de bloqueo, existe un protocolo específico de 7 días que puede
              reactivarlo.
            </p>
            <p data-id="element-277">A continuación verás exactamente qué incluye y cómo funciona.</p>
          </div>

          <div className="bg-mint-green/20 rounded-xl p-6 mb-10 border border-mint-green/50" data-id="element-278">
            <h4 className="font-serif font-bold text-lg text-dark-text mb-4" data-id="element-279">
              🕐 Qué esperar en tu caso:
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-280">
              <div className="flex flex-col" data-id="element-281">
                <span className="font-bold text-forest-green" data-id="element-282">24-48h</span>
                <span className="text-sm text-dark-text" data-id="element-283">
                  Primera señal de cambio
                </span>
              </div>
              <div className="flex flex-col" data-id="element-284">
                <span className="font-bold text-forest-green" data-id="element-285">3-5d</span>
                <span className="text-sm text-dark-text" data-id="element-286">
                  Sueño profundo reparador
                </span>
              </div>
              <div className="flex flex-col" data-id="element-287">
                <span className="font-bold text-forest-green" data-id="element-288">7-10d</span>
                <span className="text-sm text-dark-text" data-id="element-289">
                  Reducción de bochornos
                </span>
              </div>
              <div className="flex flex-col" data-id="element-290">
                <span className="font-bold text-forest-green" data-id="element-291">14-21d</span>
                <span className="text-sm text-dark-text" data-id="element-292">
                  Claridad mental recuperada
                </span>
              </div>
            </div>
          </div>

          <button onClick={onContinue} className="w-full bg-forest-green text-white py-4 px-6 rounded-xl font-semibold text-[1.1rem] flex items-center justify-center gap-2 hover:bg-[#0f4430] transition-colors shadow-lg" data-id="element-293">
            VER MI PROTOCOLO <ArrowRight className="w-5 h-5" data-id="element-294" />
          </button>
        </div>
      </motion.div>
    </div>;
};